import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { ProfileHero } from '../../components/ecosystem/ProfileHero';
import { InitiativeSection } from '../../components/initiatives/InitiativeSection';
import { LinkList } from '../../components/layout/LinkList';
import { calmirBenefits, calmirComponents, ecosystemEntries } from '../../data/ecosystem';
import { fadeUp } from '../../utils/motion';
const entry = ecosystemEntries.find(item => item.slug === 'calmir')!;
export function CalmirPage() {
  return <>
      <ProfileHero entry={entry} subtitle="Shared infrastructure. Independent organizations." intro="The CalMiR ecosystem brings independently owned organizations together around research and related institutional work. Its shared infrastructure approach uses SyNeural technology with separate workspaces, permissions and data governance for each organization." stage="Workspace design and prototype development are underway." primary={{
      label: 'Explore the approach',
      href: '#approach'
    }} secondary={{
      label: 'Explore the Research solution',
      href: '/solutions#scientific-research'
    }} data-id="element-2965" data-mp="33cvmis8scald" />

      <InitiativeSection id="approach" tone="muted" title="Connected infrastructure, independent operations" intro={['Shared technology gives each organization a common foundation for knowledge and workflows while preserving independent ownership and operational responsibility. Dedicated workspaces support each organization’s work, with clearly defined permissions and rules for any information shared across the ecosystem.']} data-id="element-2966" data-mp="1d8ksk99emcpi">
        <motion.ul {...fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2967" data-mp="215qka1ugm3s4">
          {calmirBenefits.map(benefit => <li key={benefit.title} className="bg-white border border-line rounded-lg p-8" data-id="element-2968" data-mp="2oujhe1mtihob">
              <h3 className="font-heading font-bold text-xl text-ink-dark mb-3" data-id="element-2969" data-mp="2tncnotxxpy2i">{benefit.title}</h3>
              <p className="text-ink-muted leading-relaxed" data-id="element-2970" data-mp="33h8g4198wqtn">{benefit.body}</p>
            </li>)}
        </motion.ul>
      </InitiativeSection>

      <InitiativeSection id="syneural-support" title="How SyNeural supports the ecosystem" intro={['Each organization uses the parts of the architecture its work requires. Capability assessment is added only where a specific program calls for it.']} data-id="element-2971" data-mp="2yhdrk4cy0dbr">
        <motion.div {...fadeUp} data-id="element-2972" data-mp="2kl9wy9y5q2yr">
          <LinkList columns={2} label="SyNeural components supporting the CalMiR ecosystem" items={calmirComponents.map(component => ({
          title: component.name,
          description: component.body,
          href: component.href
        }))} data-id="element-2973" data-mp="2fsgtdpefd0o9" />
        </motion.div>
      </InitiativeSection>

      <InitiativeSection id="current-focus" tone="muted" title="Current focus" intro={['Current work focuses on institutional profiles, affiliate responsibilities, workspace prototypes and the architecture for shared technology with separate organizational governance.']} data-id="element-2974" data-mp="1wtut2cqqfdle">
        <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-16" data-id="element-2975" data-mp="m9qcl3yvaxpm">
          <p className="lg:col-start-6 lg:col-span-7 border-l-2 border-teal pl-4 text-ink-muted leading-relaxed" data-id="element-2976" data-mp="qc3h5p1vopkt">
            CalMiR is also SyNeural’s main investor.{' '}
            <a href="/investors" className="font-semibold text-navy hover:text-navy-light transition-colors duration-150" data-id="element-2977" data-mp="3povrk37cg9y7">
              Read more on the Investors page
            </a>
            .
          </p>
        </motion.div>
      </InitiativeSection>

      <InitiativeSection id="related-solutions" title="Explore related solutions" data-id="element-2978" data-mp="7dir80bq3q8r">
        <motion.div {...fadeUp} data-id="element-2979" data-mp="36z5xjibens7c">
          <LinkList columns={2} label="Related solutions" items={[{
          title: 'Scientific Research',
          description: 'Follow findings to their source passages, compare studies and build a reviewed knowledge base.',
          href: '/solutions#scientific-research'
        }, {
          title: 'How It Works',
          description: 'The layers behind Noetivra, from connected knowledge to dedicated workspaces.',
          href: '/how-it-works'
        }]} data-id="element-2980" data-mp="36zywvy81bffg" />
        </motion.div>
      </InitiativeSection>

      <section aria-labelledby="calmir-cta" className="bg-navy-deep text-white py-20 lg:py-28" data-id="element-2981" data-mp="35pdleanurkq8">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2982" data-mp="3km4p2w4k7iln">
          <h2 id="calmir-cta" className="font-heading font-bold text-4xl lg:text-5xl tracking-tight mb-10" data-id="element-2983" data-mp="3fmibumoqe2cu">
            Connect your organization’s knowledge and workflows.
          </h2>
          <a href="/contact" className="inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal" data-id="element-2984" data-mp="1l9acqitq34le">
            Discuss an implementation <ArrowRight size={18} aria-hidden="true" data-id="element-2985" data-mp="3s5dzmsosxnca" />
          </a>
        </motion.div>
      </section>
    </>;
}